import { Component, ElementRef, EventEmitter, OnInit, Output } from "@angular/core"
import { environment } from "@env/environment"
import { AuthService } from "@app/auth"
import { Utils } from "@shared/util/utils"

interface NavItem {
  id: string
  routerLink: string
  label: string
  icon: string
  disabled?: boolean
  styleClass?: string
  visible?: boolean
  badge?: string
  badgeStyleClass?: string
}

// parties / rôles : sous-entrées de Pièces, affichées / masquées par le chevron à droite de Pièces
const UNIT_SUB_ITEMS = ["part", "role"]
const EXPAND_TOGGLE_CLASS = "nav-expand-toggle"

@Component({
  selector: "app-side-bar",
  templateUrl: "./side-bar.component.html",
  styleUrls: ["./side-bar.component.scss"],
})
export class SideBarComponent implements OnInit {
  isMobile = Utils.isMobile()
  isDevCaching = localStorage["caching"] || false
  unitExpanded = localStorage.getItem("sidebarUnitExpanded") === "true"

  public readonly navAllItems: Array<NavItem> = [
    {
      id: "planning",
      routerLink: "/planning",
      label: "Plannings",
      icon: "pi pi-calendar",
    },
    {
      id: "person",
      routerLink: "/person/list",
      label: "Personnes",
      icon: "pi pi-users",
    },
    {
      id: "communication2",
      routerLink: "/person/communication-person",
      label: "Communication",
      icon: "pi pi-volume-up",
    },
    // {
    //   id: "organization",
    //   routerLink: "/organization",
    //   label: "Structures",
    //   icon: "pi pi-building",
    // },
    {
      id: "crm",
      routerLink: "/crm",
      label: "Droits",
      icon: "pi pi-lock",
    },
    {
      id: "performance",
      routerLink: "/performance",
      label: "Représentations",
      icon: "pi pi-star-fill",
    },
    {
      id: "program",
      routerLink: "/program",
      label: "Programmes",
      icon: "pi pi-bookmark-fill",
    },
    {
      id: "unit",
      routerLink: "/unit",
      label: "Pièces",
      icon: "pi pi-bars",
      // pièces / parties / rôles sont liés : regroupés visuellement
      styleClass: "nav-group",
      // chevron affiché via le badge du menu (seul emplacement libre de l'entrée PrimeNG)
      badge: " ",
    },
    {
      id: "part",
      routerLink: "/part",
      label: "Parties",
      icon: "pi pi-list",
      styleClass: "nav-group nav-sub",
    },
    {
      id: "role",
      routerLink: "/role",
      label: "Rôles",
      icon: "pi pi-id-card",
      styleClass: "nav-group nav-sub",
    },
    {
      id: "unitV1",
      routerLink: "/unitV1",
      label: "Pièces - legacy",
      icon: "pi pi-bars",
      styleClass: "nav-legacy",
    },
    {
      id: "seniority",
      routerLink: "/seniority",
      label: "Ancienneté",
      icon: "pi pi-history",
    },
    {
      id: "referential",
      routerLink: "/referential",
      label: "Référentiels",
      icon: "pi pi-cog",
    },
    {
      id: "",
      routerLink: "",
      label: "",
      icon: "",
    },
    {
      id: "extern",
      routerLink: "/extern-admin",
      label: "Vue externe",
      icon: "pi pi-eye",
    },
  ].map((x: any) => {
    if (x.id !== "extern") x.disabled = this.authService.isOffline
    if (UNIT_SUB_ITEMS.includes(x.id)) x.visible = this.unitExpanded
    if (x.id === "unit") x.badgeStyleClass = this.expandToggleClass()
    return x
  })

  @Output() opening = new EventEmitter()
  navItems: Array<NavItem> = []
  sideBarShown = false
  platform = environment.production ? "" : "DEV"

  constructor(public authService: AuthService, elementRef: ElementRef<HTMLElement>) {
    // clic sur le chevron : intercepté en phase de capture pour ne pas naviguer vers les pièces
    elementRef.nativeElement.addEventListener(
      "click",
      (event) => {
        if ((event.target as HTMLElement)?.classList?.contains(EXPAND_TOGGLE_CLASS)) {
          event.preventDefault()
          event.stopPropagation()
          this.toggleUnitExpanded()
        }
      },
      true
    )
    this.navItems = this.navAllItems
    this.sideBarShown = localStorage.getItem("sidebarOpened") === "true"
    this.authService.onlineStatusUpdated$.subscribe((isOffline) => {
      this.navItems.forEach((item) => {
        if (item.id !== "extern") item.disabled = isOffline
      })
      this.navItems = [...this.navItems]
    })
  }

  ngOnInit(): void {
    this.authService.getUserInfo().then(() => {
      if (this.authService.hasRolePlanning()) {
        this.navItems = this.navItems.filter((x) => ["performance", "planning", "extern", ""].includes(x.id))
      }
      if (!this.authService.currentUser?.id) {
        this.navItems = this.navItems.filter((x) => x.id !== "extern")
      }
    })
  }

  toggle(): void {
    this.sideBarShown = !this.sideBarShown
    this.opening.emit(this.sideBarShown)
    localStorage.setItem("sidebarOpened", `${this.sideBarShown}`)
  }

  menuClick(): void {
    if (this.isMobile) {
      this.sideBarShown = false
    }
  }

  toggleUnitExpanded(): void {
    this.unitExpanded = !this.unitExpanded
    localStorage.setItem("sidebarUnitExpanded", `${this.unitExpanded}`)
    this.navItems.forEach((item) => {
      if (UNIT_SUB_ITEMS.includes(item.id)) item.visible = this.unitExpanded
      if (item.id === "unit") item.badgeStyleClass = this.expandToggleClass()
    })
    this.navItems = [...this.navItems]
  }

  private expandToggleClass(): string {
    return `${EXPAND_TOGGLE_CLASS} pi ${this.unitExpanded ? "pi-chevron-down" : "pi-chevron-right"}`
  }

  toggleDevCaching() {
    this.isDevCaching = !this.isDevCaching
    localStorage["caching"] = this.isDevCaching ? true : ""
    window.location.reload()
  }
}
